// react and js
import * as Yup from "yup";
import PropTypes from "prop-types";
import { React, useContext, useState, useRef, useEffect } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useFormik, Form, FormikProvider } from "formik";
import { useTranslation, Trans } from "react-i18next";

// mui
import {
  Link,
  IconButton,
  InputAdornment,
  Collapse,
  Card,
  Box,
  Typography,
  Grid,
  CircularProgress,
} from "@mui/material";

// components
import Iconify from "../../components/Iconify";
import backendRequest from "../../components/BackendRequest";

// hooks
import appContext from "../../hooks/appContext";

// layouts

// pages

// theme
import {
  AuthenticationButton,
  AuthenticationTextField,
  AuthenticationAlertWarning,
  AuthenticationAlertError,
  AuthenticationAlertSuccess,
  ModalContentSwitch,
  B3Tiny,
} from "../../theme/styled";

// utils
import { combineReturnMessage, TranslatedMessage } from "../../utils/common";
import { localizeExternalUrl } from "../../i18n/languages";
import {
  executeRecaptchaV3,
  preloadRecaptchaV3,
} from "../../utils/recaptchaV3";

// widgets

// app
import { myLocalSettingStorage } from "../../hooks/myLocalSettingStorage";

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
const STRENGTH_COLORS = {
  weak: "#E53935",
  medium: "#F19B37",
  strong: "#4CAF50",
};
const STRENGTH_LABEL_KEYS = {
  weak: "login.password.strengthWeak",
  medium: "login.password.strengthMedium",
  strong: "login.password.strengthStrong",
};
const STRENGTH_WIDTHS = { weak: "33%", medium: "66%", strong: "100%" };

function getPasswordStrength(metCount) {
  if (metCount <= 2) return "weak";
  if (metCount <= 3) return "medium";
  return "strong";
}

function PasswordRequirements({ password }) {
  const { t } = useTranslation();
  const passwordValue = password || "";
  const checks = [
    {
      met: passwordValue.length >= 8,
      label: t("login.password.requirementMinLength"),
    },
    {
      met: /[A-Z]/.test(passwordValue),
      label: t("login.password.requirementUppercase"),
    },
    {
      met: /[a-z]/.test(passwordValue),
      label: t("login.password.requirementLowercase"),
    },
    {
      met: /[0-9]/.test(passwordValue),
      label: t("login.password.requirementNumber"),
    },
  ];
  const metCount = checks.filter((c) => c.met).length;
  const strength =
    passwordValue.length === 0 ? null : getPasswordStrength(metCount);

  return (
    <Box sx={{ mt: 1, mb: 1.5 }}>
      {strength && (
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: "#E0E0E0",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                borderRadius: 2,
                backgroundColor: STRENGTH_COLORS[strength],
                width: STRENGTH_WIDTHS[strength],
                transition:
                  "width 0.3s ease, background-color 0.3s ease",
              }}
            />
          </Box>
          <Typography
            variant="contentSmall"
            sx={{
              color: STRENGTH_COLORS[strength],
              mt: 0.5,
              display: "block",
            }}
          >
            {t(STRENGTH_LABEL_KEYS[strength])}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-start",
          alignItems: "center",
          rowGap: 0.5,
          columnGap: 1.5,
        }}
      >
        {checks.map(({ met, label }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Iconify
              icon={
                met
                  ? "eva:checkmark-circle-2-fill"
                  : "eva:radio-button-off-outline"
              }
              sx={{ fontSize: 14, color: met ? "#4CAF50" : "#BDBDBD" }}
            />
            <Typography
              variant="contentSmall"
              sx={{ color: met ? "#4CAF50" : "#9E9E9E" }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------
// LoginForm
// ----------------------------------------------------------------------
function LoginForm({ onSwitchToCreate, onUnapprovedLogin }) {
  const { t } = useTranslation();
  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const wrapperRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [success, setSuccess] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const [forcedLoggedOut, setForcedLoggedOut] = useState(false);

  useEffect(() => {
    preloadRecaptchaV3();
  }, []);

  const localStorageForcedLoggedOut = localStorage.getItem("forcedLoggedOut");
  if (localStorageForcedLoggedOut !== null) {
    if (localStorageForcedLoggedOut === "true" && forcedLoggedOut !== true) {
      setForcedLoggedOut(true);
    }

    if (localStorageForcedLoggedOut === "false" && forcedLoggedOut !== false) {
      setForcedLoggedOut(false);
    }
  }

  useEffect(() => {
    if (!warning) return undefined;
    const timerId = setTimeout(() => setWarning(false), 60000);
    return () => clearTimeout(timerId);
  }, [warning]);

  useEffect(() => {
    if (!error) return undefined;
    const timerId = setTimeout(() => setError(false), 60000);
    return () => clearTimeout(timerId);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timerId = setTimeout(() => setSuccess(false), 8000);
    return () => clearTimeout(timerId);
  }, [success]);

  useEffect(() => {
    if (!forcedLoggedOut) return undefined;
    const timerId = setTimeout(() => {
      setForcedLoggedOut(false);
      localStorage.setItem("forcedLoggedOut", JSON.stringify(false));
    }, 60000);
    return () => clearTimeout(timerId);
  }, [forcedLoggedOut]);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("login.form.validation.emailInvalid"))
      .required(t("login.form.validation.emailRequired")),
    password: Yup.string().required(
      t("login.form.validation.passwordRequired")
    ),
  });

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleLogin = async (values, setSubmitting) => {
    if (navigator.onLine === true) {
      setError(false);
      setWarning(false);
      setSuccess(false);

      const recaptchaValue = await executeRecaptchaV3("login");

      const sendData = {
        request_type: "dashboard",
        route_info: "authentication",
        client: {
          mode: "login",
          email: values.email,
          password: values.password,
          request_known_accounts: "0",
          recaptcha_value: recaptchaValue,
        },
      };
      backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData)
        .then((contentFromBackend) => {
          const keyOnly = Object.keys(contentFromBackend);
          const checkKey = keyOnly.includes("answer");

          // Approval-pending detection: Signova-style backend returns
          // `account_state: "approval_pending"` plus a usable
          // `dashboard_access_token` so the verify view can poll. Fall back
          // to the legacy "please check your inbox" message detection.
          const accountState =
            contentFromBackend.account_state ||
            (contentFromBackend.answer && contentFromBackend.answer.account_state);
          const approvalPending =
            accountState === "approval_pending" ||
            (contentFromBackend.message &&
              contentFromBackend.message.includes("please check your inbox"));

          if (approvalPending) {
            // dashboard.php's login mode now returns a dashboard_access_token
            // alongside the approval-pending message, so the frontend can
            // forward the user straight to the "Verify your email" view and
            // poll is_email_approved against dashboard.php — same path as
            // the create-account flow.
            const approvalToken =
              contentFromBackend.dashboard_access_token ||
              (contentFromBackend.answer &&
                contentFromBackend.answer.dashboard_access_token) ||
              null;
            setSubmitting(false);
            onUnapprovedLogin(values.email, values.password, approvalToken);
            return;
          }

          if (checkKey === true) {
            setStateStorage((previousState) => ({
              ...previousState,
              authenticated: true,
              userInformation: contentFromBackend,
            }));
            localStorage.setItem(
              "myLocalStateStorage",
              JSON.stringify(contentFromBackend)
            );

            // Action mode
            let myLocalAction = localStorage.getItem("myLocalAction");
            if (myLocalAction != null) {
              myLocalAction = JSON.parse(myLocalAction);
              if (
                myLocalAction.dashboardId !==
                contentFromBackend.account_settings.config_data
                  .config_data_client.dashboard_id
              ) {
                localStorage.removeItem("myLocalAction");
              }
            }

            localStorage.setItem("myReauthentication", false);

            const myLocalSettingStorageVerify = JSON.parse(
              localStorage.getItem("myLocalSettingStorage")
            );

            if (myLocalSettingStorageVerify === null) {
              localStorage.setItem(
                "myLocalSettingStorage",
                JSON.stringify(myLocalSettingStorage)
              );
            }
          } else {
            setStateStorage((previousState) => ({
              ...previousState,
              authenticated: false,
            }));

            combineReturnMessage(contentFromBackend, setReturnMessage);
            setWarning(true);
          }

          setSubmitting(false);
        })
        .catch(() => {
          if (navigator.onLine === true) {
            setReturnMessage(<TranslatedMessage tKey="login.errors.unknown" />);
            setError(true);
            setSubmitting(false);
          }
        });
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: LoginSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleLogin(values, setSubmitting);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  const isFirstValuesRender = useRef(true);
  useEffect(() => {
    if (isFirstValuesRender.current) {
      isFirstValuesRender.current = false;
      return;
    }
    setWarning(false);
    setError(false);
    setForcedLoggedOut(false);
    localStorage.setItem("forcedLoggedOut", JSON.stringify(false));
  }, [values.email, values.password]);

  return (
    <Box sx={{ p: 4 }} dir="ltr">
      <Typography
        variant="h2"
        sx={{
          mt: 0,
          mb: 0,
        }}
      >
        {t("login.form.title")}
      </Typography>

      <Box sx={{ mt: 1, mb: 1 }} dir="ltr">
        <Typography
          variant="contentMiddle"
          sx={{ mt: 1, textAlign: "center" }}
        >
          {t("login.form.subtitle")}
        </Typography>
      </Box>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Typography
            variant="contentSmallBold"
            sx={{
              color:
                Boolean(touched.email && errors.email) === true
                  ? "red"
                  : "none",
            }}
          >
            {Boolean(touched.email && errors.email) === true
              ? errors.email
              : t("login.form.emailLabel")}
          </Typography>

          <AuthenticationTextField
            fullWidth
            hiddenLabel
            variant="outlined"
            size="small"
            type="email"
            {...getFieldProps("email")}
            error={Boolean(touched.email && errors.email)}
            sx={{ mt: "4px", mb: 1 }}
            InputLabelProps={{ shrink: false }}
          />

          <Typography
            variant="contentSmallBold"
            sx={{
              color:
                Boolean(touched.password && errors.password) === true
                  ? "red"
                  : "none",
            }}
          >
            {Boolean(touched.password && errors.password) === true
              ? errors.password
              : t("login.form.passwordLabel")}
          </Typography>

          <AuthenticationTextField
            autoComplete="off" // Avoids chrome warning
            fullWidth
            hiddenLabel
            variant="outlined"
            size="small"
            type={showPassword ? "text" : "password"}
            {...getFieldProps("password")}
            error={Boolean(touched.password && errors.password)}
            sx={{ mt: "4px", mb: 1 }}
            InputLabelProps={{ shrink: false }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Iconify
                      icon={
                        showPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                      }
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Collapse
            ref={wrapperRef}
            in={warning}
            timeout="auto"
            unmountOnExit
          >
            <AuthenticationAlertError
              severity="warning"
              sx={{ mt: 1, mb: 0 }}
            >
              {returnMessage}
            </AuthenticationAlertError>
          </Collapse>

          <Collapse
            ref={wrapperRef}
            in={error}
            timeout="auto"
            unmountOnExit
          >
            <AuthenticationAlertWarning
              severity="error"
              sx={{ mt: 1, mb: 0 }}
            >
              {returnMessage}
            </AuthenticationAlertWarning>
          </Collapse>

          <Collapse
            ref={wrapperRef}
            in={success}
            timeout="auto"
            unmountOnExit
          >
            <AuthenticationAlertSuccess
              severity="success"
              sx={{ mt: 1, mb: 0 }}
            >
              {returnMessage}
            </AuthenticationAlertSuccess>
          </Collapse>

          <Collapse
            ref={wrapperRef}
            in={forcedLoggedOut}
            timeout="auto"
            unmountOnExit
          >
            <AuthenticationAlertWarning
              severity="warning"
              sx={{ mt: 1, mb: 0 }}
            >
              {t("login.errors.forcedLoggedOut")}
            </AuthenticationAlertWarning>
          </Collapse>

          <Grid container spacing={2} sx={{ mt: 0, mb: 0 }}>
            <Grid item xs={6}>
              <B3Tiny
                fullWidth
                variant="outlined"
                type="button"
                onClick={onSwitchToCreate}
                sx={{
                  fontSize: (theme) => theme.typography.b1Small.fontSize,
                  fontWeight: (theme) => theme.typography.b1Small.fontWeight,
                  lineHeight: (theme) => theme.typography.b1Small.lineHeight,
                  letterSpacing: (theme) =>
                    theme.typography.b1Small.letterSpacing,
                }}
              >
                {t("login.form.createNewProfile")}
              </B3Tiny>
            </Grid>
            <Grid item xs={6}>
              <B3Tiny
                fullWidth
                variant="outlined"
                component={RouterLink}
                to="/password-reset"
                sx={{
                  fontSize: (theme) => theme.typography.b1Small.fontSize,
                  fontWeight: (theme) => theme.typography.b1Small.fontWeight,
                  lineHeight: (theme) => theme.typography.b1Small.lineHeight,
                  letterSpacing: (theme) =>
                    theme.typography.b1Small.letterSpacing,
                }}
              >
                {t("login.form.resetPassword")}
              </B3Tiny>
            </Grid>
          </Grid>

          <AuthenticationButton
            fullWidth
            variant="contained"
            loading={isSubmitting}
            type="submit"
            sx={{ mt: 1 }}
          >
            {t("login.form.submit")}
          </AuthenticationButton>
        </Form>
      </FormikProvider>
    </Box>
  );
}

// ----------------------------------------------------------------------
// CreateAccountForm
// ----------------------------------------------------------------------
function CreateAccountForm({ onSwitchToLogin, onCreated }) {
  const { t, i18n } = useTranslation();
  const privacyPolicyUrl = localizeExternalUrl(
    process.env.REACT_APP_URL_FRONTEND_PRIVACY_POLICY,
    i18n.language
  );
  const termsUrl = localizeExternalUrl(
    process.env.REACT_APP_URL_FRONTEND_TERMS_AND_CONDITIONS,
    i18n.language
  );
  const wrapperRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [success, setSuccess] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const [formPpTac, setFormPpTac] = useState(false);

  useEffect(() => {
    preloadRecaptchaV3();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setError(false);
        setWarning(false);
        setSuccess(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside, {
      passive: false,
      capture: false,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef]);

  const ValidationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("login.create.validation.emailInvalid"))
      .required(t("login.create.validation.emailRequired")),
    password: Yup.string()
      .required(t("login.create.validation.passwordRequired"))
      .min(8, t("login.create.validation.passwordMin"))
      .max(64, t("login.create.validation.passwordMax"))
      .matches(/[0-9]/, t("login.create.validation.passwordNumber"))
      .matches(/[a-z]/, t("login.create.validation.passwordLowercase"))
      .matches(/[A-Z]/, t("login.create.validation.passwordUppercase")),
    passwordRepeat: Yup.string()
      .oneOf(
        [Yup.ref("password"), null],
        t("login.create.validation.passwordsMustMatch")
      )
      .required(t("login.create.validation.passwordRepeatRequired")),
  });

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleCreate = async (values, setSubmitting) => {
    setError(false);
    setWarning(false);
    setSuccess(false);

    const recaptchaValue = await executeRecaptchaV3("create_account");

    const sendData = {
      request_type: "checkout",
      route_info: "authentication",
      client: {
        mode: "create_distribution",
        forename: "",
        surname: "",
        email: values.email,
        password: values.password,
        legal_agreement: formPpTac ? "1" : "0",
        recaptcha_value: recaptchaValue,
      },
    };

    backendRequest(process.env.REACT_APP_URL_API_CHECKOUT, sendData)
      .then((contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("answer");

        if (checkKey === true) {
          setSubmitting(false);
          onCreated(contentFromBackend);
        } else {
          combineReturnMessage(contentFromBackend, setReturnMessage);
          setError(true);
          setSubmitting(false);
        }
      })
      .catch(() => {
        if (navigator.onLine === true) {
          setReturnMessage(<TranslatedMessage tKey="login.errors.unknown" />);
          setError(true);
          setSubmitting(false);
        }
      });
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      passwordRepeat: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleCreate(values, setSubmitting);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  return (
    <Box sx={{ p: 4 }} dir="ltr">
      <Typography
        variant="h2"
        sx={{
          mt: 0,
          mb: 0,
        }}
      >
        {t("login.create.title")}
      </Typography>

      <Box sx={{ mt: 1, mb: 1 }} dir="ltr">
        <Typography variant="contentMiddle" sx={{ mt: 1, textAlign: "center" }}>
          {t("login.create.subtitle")}
        </Typography>
      </Box>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Typography
            variant="contentSmallBold"
            sx={{
              color:
                Boolean(touched.email && errors.email) === true
                  ? "red"
                  : "none",
            }}
          >
            {Boolean(touched.email && errors.email) === true
              ? t("login.create.validation.emailRequired")
              : t("login.create.emailLabel")}
          </Typography>

          <AuthenticationTextField
            autoComplete="new-password"
            fullWidth
            hiddenLabel
            variant="outlined"
            size="small"
            type="email"
            {...getFieldProps("email")}
            error={Boolean(touched.email && errors.email)}
            sx={{ mt: "4px", mb: 1 }}
            InputLabelProps={{ shrink: false }}
          />

          <Typography
            variant="contentSmallBold"
            sx={{
              color:
                Boolean(touched.password && errors.password) === true
                  ? "red"
                  : "none",
            }}
          >
            {Boolean(touched.password && errors.password) === true
              ? t("login.create.validation.passwordRequired")
              : t("login.create.passwordLabel")}
          </Typography>

          <AuthenticationTextField
            autoComplete="new-password"
            fullWidth
            hiddenLabel
            variant="outlined"
            size="small"
            type={showPassword ? "text" : "password"}
            {...getFieldProps("password")}
            error={Boolean(touched.password && errors.password)}
            sx={{ mt: "4px", mb: 1 }}
            InputLabelProps={{ shrink: false }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Iconify
                      icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <PasswordRequirements password={values.password} />

          <Typography
            variant="contentSmallBold"
            sx={{
              color:
                Boolean(touched.passwordRepeat && errors.passwordRepeat) ===
                true
                  ? "red"
                  : "none",
            }}
          >
            {Boolean(touched.passwordRepeat && errors.passwordRepeat) === true
              ? t("login.create.validation.passwordRepeatRequiredShort")
              : t("login.create.passwordRepeatLabel")}
          </Typography>

          <AuthenticationTextField
            autoComplete="new-password"
            fullWidth
            hiddenLabel
            variant="outlined"
            size="small"
            type={showPassword ? "text" : "password"}
            {...getFieldProps("passwordRepeat")}
            error={Boolean(touched.passwordRepeat && errors.passwordRepeat)}
            sx={{ mt: "4px", mb: 1 }}
            InputLabelProps={{ shrink: false }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Iconify
                      icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="contentSmall"
              sx={{ flex: 1, lineHeight: 1.4 }}
            >
              <Trans i18nKey="login.create.legalAgreement">
                I confirm that I have read and agree with the
                <Link
                  href={privacyPolicyUrl}
                  target="_blank"
                  underline="none"
                  variant="contentSmallLink"
                  color="inherit"
                >
                  {` privacy policy `}
                </Link>
                and
                <Link
                  href={termsUrl}
                  target="_blank"
                  underline="none"
                  variant="contentSmallLink"
                  color="inherit"
                >
                  {` terms and conditions`}
                </Link>
                .
              </Trans>
            </Typography>

            <ModalContentSwitch
              checked={formPpTac}
              onChange={(event) => {
                setFormPpTac(event.target.checked);
              }}
              inputProps={{ "aria-label": "controlled" }}
              sx={{ flexShrink: 0 }}
            />
          </Box>

          {/* All spacing lives on the alert. When collapsed: no gap below
              "I confirm". When shown: alert provides 2u above + 2u below
              itself (privacy mb=0, back mt=0), visually symmetric. */}
          <Collapse ref={wrapperRef} in={warning} timeout="auto" unmountOnExit>
            <AuthenticationAlertError severity="warning" sx={{ mt: 0, mb: 2 }}>
              {returnMessage}
            </AuthenticationAlertError>
          </Collapse>

          <Collapse ref={wrapperRef} in={error} timeout="auto" unmountOnExit>
            <AuthenticationAlertError severity="error" sx={{ mt: 0, mb: 2 }}>
              {returnMessage}
            </AuthenticationAlertError>
          </Collapse>

          <Collapse ref={wrapperRef} in={success} timeout="auto" unmountOnExit>
            <AuthenticationAlertSuccess severity="success" sx={{ mt: 0, mb: 2 }}>
              {returnMessage}
            </AuthenticationAlertSuccess>
          </Collapse>

          <B3Tiny
            fullWidth
            variant="outlined"
            onClick={onSwitchToLogin}
            sx={{
              mt: 0,
              mb: 1,
              fontSize: (theme) => theme.typography.b1Small.fontSize,
              fontWeight: (theme) => theme.typography.b1Small.fontWeight,
              lineHeight: (theme) => theme.typography.b1Small.lineHeight,
              letterSpacing: (theme) => theme.typography.b1Small.letterSpacing,
            }}
          >
            {t("login.create.backToLogin")}
          </B3Tiny>

          <AuthenticationButton
            fullWidth
            variant="contained"
            disabled={!formPpTac}
            loading={isSubmitting}
            type="submit"
            sx={{ mt: 0 }}
          >
            {t("login.create.submit")}
          </AuthenticationButton>
        </Form>
      </FormikProvider>
    </Box>
  );
}

// ----------------------------------------------------------------------
// ApprovalAwaiting
// ----------------------------------------------------------------------
function ApprovalAwaiting({
  email,
  createResponse,
  pendingToken,
  pendingPassword,
  onBack,
}) {
  const { t } = useTranslation();
  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [resendError, setResendError] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);
  const approvedRef = useRef(false);

  const dashboardAccessToken =
    (createResponse &&
      createResponse.answer &&
      createResponse.answer.dashboard_access_token) ||
    pendingToken ||
    "";

  // Create-account flow gets its token from checkout.php, so poll that.
  // Unapproved-login flow gets its token from dashboard.php's login mode,
  // so poll that. Both use the same wrapped-envelope payload shape.
  const isLoginRedirect = !createResponse;
  const pollApiUrl = isLoginRedirect
    ? process.env.REACT_APP_URL_API_DASHBOARD
    : process.env.REACT_APP_URL_API_CHECKOUT;
  const pollRequestType = isLoginRedirect ? "dashboard" : "checkout";

  const finishAutoLogin = (userInformation) => {
    setStateStorage((previousState) => ({
      ...previousState,
      authenticated: true,
      userInformation,
    }));
    localStorage.setItem(
      "myLocalStateStorage",
      JSON.stringify(userInformation)
    );

    const dashboardId =
      userInformation &&
      userInformation.account_settings &&
      userInformation.account_settings.config_data &&
      userInformation.account_settings.config_data.config_data_client &&
      userInformation.account_settings.config_data.config_data_client
        .dashboard_id;

    let myLocalAction = localStorage.getItem("myLocalAction");
    if (myLocalAction != null) {
      myLocalAction = JSON.parse(myLocalAction);
      if (dashboardId && myLocalAction.dashboardId !== dashboardId) {
        localStorage.removeItem("myLocalAction");
      }
    }

    localStorage.setItem("myReauthentication", false);

    const myLocalSettingStorageVerify = JSON.parse(
      localStorage.getItem("myLocalSettingStorage")
    );

    if (myLocalSettingStorageVerify === null) {
      localStorage.setItem(
        "myLocalSettingStorage",
        JSON.stringify(myLocalSettingStorage)
      );
    }
  };

  const performReLogin = async () => {
    if (!email || !pendingPassword) return;

    const recaptchaValue = await executeRecaptchaV3("login");

    const sendData = {
      request_type: "dashboard",
      route_info: "authentication",
      client: {
        mode: "login",
        email,
        password: pendingPassword,
        request_known_accounts: "0",
        recaptcha_value: recaptchaValue,
      },
    };

    backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData)
      .then((contentFromBackend) => {
        if (
          contentFromBackend &&
          Object.keys(contentFromBackend).includes("answer")
        ) {
          finishAutoLogin(contentFromBackend);
        }
      })
      .catch(() => {});
  };

  const performAutoLogin = () => {
    if (approvedRef.current) return;
    approvedRef.current = true;

    if (createResponse) {
      finishAutoLogin(createResponse);
    } else {
      performReLogin();
    }
  };

  const checkApproval = () => {
    if (approvedRef.current || !dashboardAccessToken) return;

    const sendData = {
      request_type: pollRequestType,
      route_info: "authentication",
      client: {
        mode: "is_email_approved",
        dashboard_access_token: dashboardAccessToken,
      },
    };

    backendRequest(pollApiUrl, sendData)
      .then((contentFromBackend) => {
        if (!contentFromBackend) return;
        const isApproved =
          contentFromBackend.answer &&
          (contentFromBackend.answer.is_email_approved === "true" ||
            contentFromBackend.answer.is_email_approved === true);
        if (isApproved) {
          performAutoLogin();
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!dashboardAccessToken) return undefined;
    // First check after a short delay, then poll repeatedly.
    const firstId = setTimeout(checkApproval, 500);
    const intervalId = setInterval(checkApproval, 1500);
    return () => {
      clearTimeout(firstId);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardAccessToken]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timerId = setTimeout(() => {
      setResendCooldown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);
    return () => clearTimeout(timerId);
  }, [resendCooldown]);

  useEffect(() => {
    if (!resendSuccess) return undefined;
    const timerId = setTimeout(() => setResendSuccess(false), 8000);
    return () => clearTimeout(timerId);
  }, [resendSuccess]);

  const handleResend = async () => {
    if (!email || resendCooldown > 0) return;
    setResendError("");
    setResendSuccess(false);
    setResendCooldown(60);

    const recaptchaValue = await executeRecaptchaV3("resend_approval_email");

    const sendData = {
      request_type: pollRequestType,
      route_info: "authentication",
      client: {
        mode: "resend_approval_email_alt",
        email,
        recaptcha_value: recaptchaValue,
      },
    };

    backendRequest(pollApiUrl, sendData)
      .then((contentFromBackend) => {
        if (contentFromBackend && contentFromBackend.answer) {
          setResendSuccess(true);
        } else {
          setResendError(<TranslatedMessage tKey="login.approval.resendError" />);
        }
      })
      .catch(() => {
        setResendError(<TranslatedMessage tKey="login.approval.resendError" />);
      });
  };

  return (
    <Box sx={{ p: 4 }} dir="ltr">
      <Typography variant="h2" sx={{ mt: 0, mb: 0 }}>
        {t("login.approval.title")}
      </Typography>

      <Box sx={{ mt: 2, mb: 1.5 }} dir="ltr">
        <Typography variant="contentMiddle">
          <Trans
            i18nKey="login.approval.sentTo"
            values={{ email }}
            components={{
              1: (
                <Typography component="span" variant="contentMiddleBold" />
              ),
            }}
          />
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0, mb: 2 }}>
        <CircularProgress
          size={14}
          thickness={5}
          sx={{ color: "#4CB833" }}
        />
        <Typography variant="contentSmall" sx={{ color: "#9E9E9E" }}>
          {t("login.approval.waiting")}
        </Typography>
      </Box>

      <Collapse in={resendSuccess} timeout="auto" unmountOnExit>
        <AuthenticationAlertSuccess severity="success" sx={{ mb: 2 }}>
          {t("login.approval.resendSuccess")}
        </AuthenticationAlertSuccess>
      </Collapse>

      <Collapse in={Boolean(resendError)} timeout="auto" unmountOnExit>
        <AuthenticationAlertWarning severity="error" sx={{ mb: 2 }}>
          {resendError}
        </AuthenticationAlertWarning>
      </Collapse>

      <B3Tiny
        fullWidth
        variant="outlined"
        onClick={onBack}
        sx={{
          mb: 1,
          fontSize: (theme) => theme.typography.b1Small.fontSize,
          fontWeight: (theme) => theme.typography.b1Small.fontWeight,
          lineHeight: (theme) => theme.typography.b1Small.lineHeight,
          letterSpacing: (theme) => theme.typography.b1Small.letterSpacing,
        }}
      >
        {t("login.approval.backToLogin")}
      </B3Tiny>

      <AuthenticationButton
        fullWidth
        variant="contained"
        onClick={handleResend}
        disabled={resendCooldown > 0}
      >
        {resendCooldown > 0
          ? t("login.approval.resendWithCountdown", {
              seconds: resendCooldown,
            })
          : t("login.approval.resendEmail")}
      </AuthenticationButton>
    </Box>
  );
}

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------
PasswordRequirements.propTypes = {
  password: PropTypes.string,
};

LoginForm.propTypes = {
  onSwitchToCreate: PropTypes.func.isRequired,
  onUnapprovedLogin: PropTypes.func.isRequired,
};

CreateAccountForm.propTypes = {
  onSwitchToLogin: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

ApprovalAwaiting.propTypes = {
  email: PropTypes.string.isRequired,
  createResponse: PropTypes.any,
  pendingToken: PropTypes.string,
  pendingPassword: PropTypes.string,
  onBack: PropTypes.func.isRequired,
};

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
// Sub-screen of the auth form is encoded in the URL via `?view=...` so that
// a language switch (which round-trips through a full reload) preserves the
// user's current screen.
const VALID_VIEWS = ["login", "create", "approval_awaiting"];

export default function Widget() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get("view");
  const view = VALID_VIEWS.includes(rawView) ? rawView : "login";

  const setView = (next) => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        if (next === "login") {
          updated.delete("view");
        } else {
          updated.set("view", next);
        }
        return updated;
      },
      { replace: true }
    );
  };

  const [createResponse, setCreateResponse] = useState(null);
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingPassword, setPendingPassword] = useState("");
  const [pendingToken, setPendingToken] = useState("");

  // If the URL says approval_awaiting but in-memory pending state was lost
  // (e.g. after a language-switch reload), fall back to login. The
  // approval-polling needs the token, which we deliberately don't persist.
  useEffect(() => {
    if (view === "approval_awaiting" && !pendingToken && !createResponse) {
      setView("login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approvalEmail =
    (createResponse &&
      createResponse.account_settings &&
      createResponse.account_settings.config_data &&
      createResponse.account_settings.config_data.config_data_client &&
      createResponse.account_settings.config_data.config_data_client.email) ||
    pendingEmail ||
    "";

  const clearPending = () => {
    setPendingEmail("");
    setPendingPassword("");
    setPendingToken("");
  };

  let cardContent = null;
  if (view === "login") {
    cardContent = (
      <LoginForm
        onSwitchToCreate={() => setView("create")}
        onUnapprovedLogin={(email, password, token) => {
          setCreateResponse(null);
          setPendingEmail(email);
          setPendingPassword(password);
          setPendingToken(token || "");
          setView("approval_awaiting");
        }}
      />
    );
  } else if (view === "create") {
    cardContent = (
      <CreateAccountForm
        onSwitchToLogin={() => setView("login")}
        onCreated={(response) => {
          clearPending();
          setCreateResponse(response);
          setView("approval_awaiting");
        }}
      />
    );
  } else if (view === "approval_awaiting") {
    cardContent = (
      <ApprovalAwaiting
        email={approvalEmail}
        createResponse={createResponse}
        pendingToken={pendingToken}
        pendingPassword={pendingPassword}
        onBack={() => {
          setCreateResponse(null);
          clearPending();
          setView("login");
        }}
      />
    );
  }

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        direction="column"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 800,
          px: 2,
        }}
      >
        <Card
          sx={{
            background: "#F2F2F2",
            borderColor: "#6D6E70",
            boxShadow: "0px 0px 5px 0px rgb(255, 255, 255)",
          }}
        >
          {cardContent}
        </Card>
      </Box>
    </Grid>
  );
}
